import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import BuildYourFile from "@/pages/BuildYourFile";
import Apply from "@/pages/Apply";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ApplyApplicants from "@/pages/ApplyApplicants";
import ApplyAddOnDetails from "@/pages/ApplyAddOnDetails";
import ApplyReview from "@/pages/ApplyReview";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";
import GuideHub from "@/pages/GuideHub";
import GuideCombination from "@/pages/GuideCombination";
import SpainVisaFromUsa from "@/pages/SpainVisaFromUsa";
import { spainUsaNationalityConfig } from "@/lib/spainUsaNationalityConfig";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import AdminContentLayout from "@/components/AdminContentLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import {
  AdminContentDestinations, AdminContentCombinations, AdminContentNationalities,
  AdminContentPosts, AdminContentAuthors, AdminContentApiKeys,
  AdminContentWebhooks, AdminContentSettings, AdminContentReviewQueue,
  AdminContentActivity,
} from "@/pages/admin/AdminContent";

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/build-your-file" element={<BuildYourFile />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/apply/applicants/:orderId" element={<ProtectedRoute><ApplyApplicants /></ProtectedRoute>} />
            <Route path="/apply/applicants/:orderId/:idx" element={<ProtectedRoute><ApplyApplicants /></ProtectedRoute>} />
            <Route path="/apply/addons/:orderId" element={<ProtectedRoute><ApplyAddOnDetails /></ProtectedRoute>} />
            <Route path="/apply/review/:orderId" element={<ProtectedRoute><ApplyReview /></ProtectedRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/schengen-visa-guide" element={<GuideHub />} />
            <Route path="/schengen-visa-guide/:destination" element={<GuideCombination />} />
            <Route path="/schengen-visa-guide/:destination/:nationality" element={<GuideCombination />} />
            {/* Spain-USA nationality-specific guides — one route per configured nationality slug.
                Adding a new nationality = adding an entry in spainUsaNationalityConfig. */}
            {Object.keys(spainUsaNationalityConfig).map((slug) => (
              <Route
                key={slug}
                path={`/spain-visa-from-usa-for-${slug}`}
                element={<SpainVisaFromUsa slug={slug} />}
              />
            ))}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/in-progress" element={<AdminOrders filterStatus="in_progress" title="In Progress" />} />
              <Route path="orders/completed" element={<AdminOrders filterStatus="completed" title="Completed" />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="team" element={<AdminUsers />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="content" element={<AdminContentLayout />}>
                <Route index element={<AdminContentDestinations />} />
                <Route path="destinations" element={<AdminContentDestinations />} />
                <Route path="combinations" element={<AdminContentCombinations />} />
                <Route path="nationalities" element={<AdminContentNationalities />} />
                <Route path="posts" element={<AdminContentPosts />} />
                <Route path="authors" element={<AdminContentAuthors />} />
                <Route path="api-keys" element={<AdminContentApiKeys />} />
                <Route path="webhooks" element={<AdminContentWebhooks />} />
                <Route path="review-queue" element={<AdminContentReviewQueue />} />
                <Route path="activity" element={<AdminContentActivity />} />
                <Route path="settings" element={<AdminContentSettings />} />
              </Route>
            </Route>
          </Routes>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
